import React, { useState } from 'react';
import { 
  Modal, 
  Box, 
  Typography, 
  Button,
  Checkbox,
  IconButton 
} from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';

// formatNumber function from InvestorsTable.js
const formatNumber = (value, decimals = 3) => {
  if (value == null) return "0";

  const n = Number(value);

  // If number is too big or too small → keep exponential
  if (Math.abs(n) >= 1e9 || (Math.abs(n) > 0 && Math.abs(n) < 1e-3)) {
    return n.toExponential(decimals); // e.g. "5.000e+21"
  }

  // Otherwise → normal fixed decimal
  return n.toFixed(decimals);
};

const UserWiseRebalanceModal = ({ 
  open, 
  onClose, 
  userWiseData, 
  selectedWashTrades,
  handleWashTradeChange,
  handleFinalConfirm,
  strategyName 
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: isFullScreen ? '50%' : '55%',
    transform: 'translate(-50%, -50%)',
    width: isFullScreen ? '100%' : '90%',
    height: isFullScreen ? '100%' : 'auto',
    maxWidth: isFullScreen ? '100%' : '1200px',
    maxHeight: isFullScreen ? '100%' : '700px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: isFullScreen ? 0 : '4px',
    display: 'flex',
    flexDirection: 'column'
  };

  // Check if any wash trades exist
  const hasWashTrades = userWiseData.some(userData => 
    userData.assets.some(asset => asset.isWashTrade)
  );

  return (
    <Modal 
      open={open} 
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={{ 
          p: 3, 
          borderBottom: '1px solid #E0E0E0',
          backgroundColor: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 400, mb: 1 }}>
              Confirm User- Wise Rebalance
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#666666' }}>
              {strategyName} 
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setIsFullScreen(!isFullScreen)}
            size="small"
            sx={{ mt: -1, mr: -1 }}
          >
            {isFullScreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
          </IconButton>
        </Box>

        {/* Content area */}
        <Box sx={{ 
          flex: 1,
          overflow: 'auto',
          p: 3,
          minHeight: 0
        }}>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {/* Left side table */}
            <Box sx={{ 
              flex: hasWashTrades ? 1.4 : 1,
              bgcolor: '#F8F9FA',
              borderRadius: '4px',
              p: 2
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 400, fontSize: '14px' }}>Investor</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 400, fontSize: '14px' }}>Asset</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 400, fontSize: '14px' }}>Current Amount ($)</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 400, fontSize: '14px' }}>New Amount ($)</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 400, fontSize: '14px' }}>Buy/Sell ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {userWiseData.map((userData) => (
                    userData.assets.map((asset, index) => (
                      <tr 
                        key={`${userData.userId}-${index}`}
                        style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#F8F9FA' }}
                      >
                        {index === 0 && (
                          <td 
                            rowSpan={userData.assets.length}
                            style={{ 
                              padding: '8px', 
                              fontSize: '14px',
                              verticalAlign: 'middle'
                            }}
                          >
                            {userData.firstName}
                          </td>
                        )}
                        <td style={{ padding: '8px', fontSize: '14px' }}>{asset.symbol}</td>
                        <td style={{ padding: '8px', textAlign: 'right', fontSize: '14px' }}>
                          ${formatNumber(asset.currentAmount, 3)}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right', fontSize: '14px' }}>
                          ${formatNumber(asset.newAmount, 3)}
                        </td>
                        <td style={{ 
                          padding: '8px', 
                          textAlign: 'right', 
                          fontSize: '14px',
                          color: parseFloat(asset.buySellAmount) < 0 ? '#D32F2F' : '#2E7D32'
                        }}>
                          {parseFloat(asset.buySellAmount) < 0 
                            ? `($${formatNumber(Math.abs(parseFloat(asset.buySellAmount)), 3)})` 
                            : `$${formatNumber(asset.buySellAmount, 3)}`}
                        </td>
                      </tr>
                    ))
                  ))}
                </tbody>
              </table>
            </Box>

            {/* Right side - Wash Sale Warning */}
            {hasWashTrades && (
              <Box sx={{ flex: 1.6 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                  Beware! Risk of Wash-Sale Trade
                </Typography>
                <Typography sx={{ fontSize: '14px', color: 'text.secondary', mb: 2 }}>
                  The following trades under your order meet conditions for "Wash Sale" trade, and may get flagged by the SEC.
                </Typography>
                <Typography sx={{ fontSize: '14px', mb: 2 }}>
                  Select the trades you'd like to proceed with anyway.
                </Typography>

                <Box sx={{ 
                  bgcolor: '#F8F9FA', 
                  p: 2, 
                  borderRadius: '4px'
                }}>
                  <Typography sx={{ fontSize: '14px', mb: 2, color: '#666666' }}>
                    {strategyName}
                  </Typography>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}></th>
                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 400, fontSize: '14px' }}>Investor</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 400, fontSize: '14px' }}>Asset</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 400, fontSize: '14px' }}>Price</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 400, fontSize: '14px' }}>Quantity</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 400, fontSize: '14px' }}>Value($)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userWiseData.map((userData) => (
                        userData.assets
                          .filter(asset => asset.isWashTrade)
                          .map((asset, index, filteredAssets) => (
                            <tr 
                              key={`${userData.userId}-${asset.assetId}-${index}`}
                              style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#F8F9FA' }}
                            >
                              <td style={{ padding: '8px' }}>
                                <Checkbox 
                                  checked={!!selectedWashTrades[`${userData.userId}-${asset.assetId}`]}
                                  onChange={() => handleWashTradeChange(userData.userId, asset.assetId)}
                                  size="small"
                                />
                              </td>
                              {index === 0 && (
                                <td 
                                  rowSpan={filteredAssets.length}
                                  style={{ 
                                    padding: '8px',
                                    fontSize: '14px',
                                    verticalAlign: 'middle'
                                  }}
                                >
                                  {userData.firstName} {userData.lastName}
                                </td>
                              )}
                              <td style={{ padding: '8px', fontSize: '14px' }}>{asset.symbol}</td>
                              <td style={{ padding: '8px', textAlign: 'right', fontSize: '14px' }}>
                                ${formatNumber(asset.price, 3)}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', fontSize: '14px' }}>
                                {formatNumber(asset.amount, 3)}
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', fontSize: '14px' }}>
                                ${formatNumber(asset.cost, 3)}
                              </td>
                            </tr>
                          ))
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ 
          p: 3, 
          borderTop: '1px solid #E0E0E0',
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 2,
          backgroundColor: '#fff'
        }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ textTransform: 'none', minWidth: '100px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFinalConfirm}
            variant="contained"
            sx={{ textTransform: 'none', minWidth: '100px', bgcolor: '#1976D2' }}
          >
            Confirm
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UserWiseRebalanceModal; 